import { Fragment, ReactElement, ReactNode, useEffect, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import clsx from 'clsx';
import { CheckCircle, X } from 'lucide-react';
export interface IModalProps {
  show: boolean;
  children?: JSX.Element | ReactElement | ReactNode | null;
  onClose?: () => void | undefined;
  title?: string;
  description?: string;
  yellowHeader?: boolean;
  modalIcon?: JSX.Element | ReactElement | ReactNode | null;
  backdrop?: boolean;
  size?:
  | 'xs'
  | 'sm'
  | 'md'
  | 'lg'
  | 'xl'
  | '2xl'
  | '3xl'
  | '4xl'
  | '5xl'
  | '6xl'
  | '7xl'
  | 'screen-xl'
  | 'screen-2xl'
  | 'fullscreen';
  scrollable?: boolean;
  showCloseButton?: boolean;
  showDescription?: boolean;
  staticMode?: boolean;
  autoCloseAfter?: number; // Param in milliseconds
  showBackButton?: boolean;
  onBack?: () => void;
  classes?: string;
  showTitleSection?: boolean;
  hidePanelStyles?: boolean;
  isHeadRounded?: boolean;
}

function Modal({
  show = false,
  onClose,
  backdrop = true,
  scrollable = false,
  size = 'md',
  children,
  showCloseButton = true,
  staticMode = false,
  autoCloseAfter,
  showBackButton,
  showDescription = true,
  onBack,
  classes,
  showTitleSection = true,
  hidePanelStyles = true,
  isHeadRounded = true,
  title,
  description,
}: IModalProps): JSX.Element {
  const cancelButtonRef = useRef(null);
  const close = (_value: boolean) => {
    staticMode ? null : onClose?.();
  };

  useEffect(() => {
    if (autoCloseAfter) {
      setTimeout(() => {
        onClose?.();
      }, autoCloseAfter);
    }
  }, []);

  return (
    <Transition appear show={show} as={Fragment}>
      <Dialog as={'div'} className="fixed z-[9999] !rounded-xl" onClose={close} initialFocus={cancelButtonRef}>
        {backdrop && (
          <Transition.Child
            as={Fragment}
            unmount={false}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0">
            <Dialog.Backdrop
              as="div"
              // className="fixed inset-0 bg-gray-500 bg-opacity-25 z-30"
              className="fixed inset-0 bg-black/40 z-[9999] backdrop-blur-md"
            />
          </Transition.Child>
        )}
        <div className="fixed inset-0 overflow-y-auto ">

          <div className="flex min-h-full items-center justify-center p-4 text-center ">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95">
              <Dialog.Panel
                className={clsx(
                  'w-full transform transition-all',
                  !hidePanelStyles ? 'rounded-xl bg-white dark:bg-darkBg text-left align-middle shadow-xl' : '',
                  { ['overflow-y-auto']: scrollable },
                  { ['max-w-xs']: size == 'xs' },
                  { ['max-w-sm']: size == 'sm' },
                  { ['max-w-md']: size == 'md' },
                  { ['max-w-lg']: size == 'lg' },
                  { ['max-w-xl']: size == 'xl' },
                  { ['max-w-2xl']: size == '2xl' },
                  { ['max-w-3xl']: size == '3xl' },
                  { ['max-w-4xl']: size == '4xl' },
                  { ['max-w-5xl']: size == '5xl' },
                  { ['max-w-6xl']: size == '6xl' },
                  { ['max-w-7xl']: size == '7xl' },
                  { ['max-w-screen-xl']: size == 'screen-xl' },
                  { ['max-w-screen-2xl']: size == 'screen-2xl' },
                  { ['h-screen']: size == 'fullscreen' },
                  classes
                )}>

                {showTitleSection &&
                  <div className={` bg-white dark:bg-darkBg ${isHeadRounded ? 'rounded-t-xl' : ''} `}>
                    <Dialog.Title as="div" className="text-lg flex items-center justify-center font-medium leading-6 text-gray-900 dark:text-darkBg-mainText   md:px-6">
                      <div className="py-3 text-[22px] flex items-center">

                        <div>
                          <h3 className="text-lg pt-3 font-medium text-gray-900 dark:text-darkBg-mainText flex items-center">
                            <CheckCircle className="h-5 w-5 mr-2 text-green-600" />
                            {title || "Submit Form"}
                          </h3>
                        </div>


                        {showCloseButton &&
                          <button
                            type="button"
                            onClick={onClose}
                            className="absolute right-4 rounded-full bg-gray-100 dark:bg-gray-700 p-1.5 text-gray-500 dark:text-gray-300  focus:outline-none  transition-colors duration-200 z-10"
                            aria-label="Close"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        }
                      </div>
                    </Dialog.Title>
                  </div>
                }

                <div>

                  {children}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
export default Modal;
